import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Input,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  Spin
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// ─────────────────────────────────────────────────────────────────
// Glass-i — ErpGridEditableCell
//
// Inline-editing wrapper for a single grid cell. Wraps the column's
// `render` output in a click target. Double-click flips the cell to
// edit mode; Enter saves; Escape cancels; blur saves.
//
// Activation rules (the grid decides; this component just trusts them):
//   - column.editable === true
//   - adapter.updateCell is wired
//   - column.locked !== true   (unless explicit override in config)
//   - caller passed `permissionToEdit` truthy
//
// Save flow:
//   1. validate(draft) — runs column.validationRules in order
//   2. if changed, call onSave(newValue) which calls
//      adapter.updateCell(id, field, value) under the hood
//   3. on success, leave edit mode and let the parent flush the new
//      value into local rows / refetch
//   4. on failure, surface the error on the cell; keep edit mode open
//      so the user can correct (Esc to revert)
//
// Read-mode visual: a hairline accent under the value on hover so
// users can tell which cells are interactive. No icons inside cells.
// ─────────────────────────────────────────────────────────────────

/**
 * Run validation rules in order. Returns the first error message or null.
 */
function runValidation(value, rules) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  for (const r of rules) {
    if (!r) continue;
    if (r.required) {
      if (value === undefined || value === null || value === '') {
        return r.message || 'This field is required.';
      }
    }
    if (typeof r.min === 'number' && typeof value === 'number' && value < r.min) {
      return r.message || `Minimum: ${r.min}`;
    }
    if (typeof r.max === 'number' && typeof value === 'number' && value > r.max) {
      return r.message || `Maximum: ${r.max}`;
    }
    if (r.pattern instanceof RegExp && value !== null && value !== undefined && !r.pattern.test(String(value))) {
      return r.message || 'Invalid value';
    }
    if (typeof r.validator === 'function') {
      const err = r.validator(value);
      if (err) return err;
    }
  }
  return null;
}

function valuesEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a instanceof Date || b instanceof Date) {
    const ta = a instanceof Date ? a.getTime() : (a ? new Date(a).getTime() : NaN);
    const tb = b instanceof Date ? b.getTime() : (b ? new Date(b).getTime() : NaN);
    return ta === tb;
  }
  return false;
}

export default function ErpGridEditableCell({
  value,
  record,
  index,
  column,
  renderRead,                 // (value, record, index) => ReactNode — the column's existing render
  onSave,                     // (newValue) => Promise<void>
  permissionToEdit = true,
  className = ''
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  // Capture the value at the moment edit started so we can revert
  // cleanly even if upstream rows mutate while editing.
  const originalRef = useRef(value);
  // Track in-flight save so an Escape during save can't leak.
  const savingRef = useRef(false);

  // Re-sync the draft if the upstream value changes while NOT editing.
  useEffect(() => {
    if (!editing) {
      setDraft(value);
    }
  }, [value, editing]);

  const enterEdit = useCallback(
    (e) => {
      if (!permissionToEdit) return;
      if (column.locked && column.allowLockedEdit !== true) return;
      // Stop the row's double-click → detail-drawer gesture from firing.
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      originalRef.current = value;
      setDraft(value);
      setError(null);
      setEditing(true);
    },
    [permissionToEdit, column.locked, column.allowLockedEdit, value]
  );

  const cancelEdit = useCallback(() => {
    if (savingRef.current) return;
    setDraft(originalRef.current);
    setError(null);
    setEditing(false);
  }, []);

  const commitEdit = useCallback(
    async (override) => {
      const next = override === undefined ? draft : override;
      const original = originalRef.current;
      const validationError = runValidation(next, column.validationRules);
      if (validationError) {
        setError(validationError);
        return;
      }
      if (valuesEqual(next, original)) {
        setEditing(false);
        setError(null);
        return;
      }
      if (typeof onSave !== 'function') {
        setEditing(false);
        return;
      }
      setSaving(true);
      savingRef.current = true;
      setError(null);
      try {
        await onSave(next);
        // Success — exit edit mode; the parent is responsible for
        // refreshing the row data (optimistic update via useErpMaster
        // or a refetch in server mode).
        setEditing(false);
      } catch (err) {
        const msg =
          (err && err.message) ||
          (err && err.description) ||
          'Save failed';
        setError(msg);
      } finally {
        setSaving(false);
        savingRef.current = false;
      }
    },
    [draft, column.validationRules, onSave]
  );

  // Focus the editor when it opens.
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      if (typeof el.focus === 'function') el.focus({ cursor: 'all' });
      else if (el.input && typeof el.input.focus === 'function') el.input.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [editing]);

  // ── editor by type ───────────────────────────────────────────
  const editorNode = useMemo(() => {
    if (!editing) return null;
    const commonProps = {
      size: 'small',
      autoFocus: true,
      disabled: saving,
      onKeyDown: (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          cancelEdit();
        }
      }
    };
    const inputProps = {
      ...commonProps,
      onBlur: () => commitEdit(),
      onPressEnter: () => commitEdit()
    };
    switch (column.editorType) {
      case 'select':
        return (
          <Select
            {...commonProps}
            ref={inputRef}
            value={draft}
            options={column.editorOptions || []}
            onChange={(v) => {
              setDraft(v);
              // Selects commit immediately on change — matches AntD's
              // single-choice UX expectation for ERP grids.
              commitEdit(v);
            }}
            popupMatchSelectWidth={false}
            className="w-full"
          />
        );
      case 'number':
        return (
          <InputNumber
            {...inputProps}
            ref={inputRef}
            value={draft}
            onChange={(v) => setDraft(v)}
            className="!w-full"
          />
        );
      case 'date':
        return (
          <DatePicker
            {...commonProps}
            ref={inputRef}
            value={draft ? dayjs(draft) : null}
            onChange={(d) => {
              const iso = d ? d.toISOString() : null;
              setDraft(iso);
              commitEdit(iso);
            }}
            className="!w-full"
          />
        );
      case 'checkbox':
        return (
          <Switch
            checked={Boolean(draft)}
            size="small"
            disabled={saving}
            onChange={(v) => {
              setDraft(v);
              commitEdit(v);
            }}
          />
        );
      case 'text':
      default:
        return (
          <Input
            {...inputProps}
            ref={inputRef}
            value={draft == null ? '' : String(draft)}
            onChange={(e) => setDraft(e.target.value)}
            allowClear
          />
        );
    }
  }, [editing, saving, draft, column.editorType, column.editorOptions, commitEdit, cancelEdit]);

  // ── read-mode shell ──────────────────────────────────────────
  if (!editing) {
    const readOutput = renderRead
      ? renderRead(value, record, index)
      : value == null
        ? null
        : String(value);
    const editable = permissionToEdit && (!column.locked || column.allowLockedEdit);
    return (
      <div
        className={`erp-grid__editable-cell ${
          editable ? 'erp-grid__editable-cell--editable' : ''
        } ${className}`}
        onDoubleClick={editable ? enterEdit : undefined}
        // Allow keyboard activation when this cell is focused via
        // tabindex (set by the grid's wrapper); Enter starts edit.
        tabIndex={editable ? 0 : -1}
        onKeyDown={(e) => {
          if (!editable) return;
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            enterEdit(e);
          }
        }}
        role={editable ? 'button' : undefined}
        aria-label={
          editable
            ? `Edit ${typeof column.title === 'string' ? column.title : 'cell'}`
            : undefined
        }
      >
        {readOutput}
      </div>
    );
  }

  // ── edit-mode shell ──────────────────────────────────────────
  // The error message renders inline below the editor (instead of a
  // hovering Tooltip) so it stays anchored to the cell on any layout
  // and is reachable by screen readers without focus-tricks. The
  // outer wrapper stops double-click from bubbling so the row's
  // detail-drawer gesture never fights with the active editor.
  return (
    <div
      className={`erp-grid__editable-cell erp-grid__editable-cell--editing ${className}`}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-1">
        <div className="flex-1 min-w-0">{editorNode}</div>
        {saving ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : null}
      </div>
      {error ? (
        <div
          className="erp-grid__editable-cell-error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      ) : null}
    </div>
  );
}
